import radialGradientBlobs from './radialGradientBlobs.glsl';
import { type BlendModes, type CanvasConfigByDevice } from './types.js';
import vertex from './vertex.glsl';
import { store } from '@/contexts/store.js';
import { useCustomThemeConfig } from '@/hooks/useCustomThemeConfig.js';
import { useMatchMedia } from '@/hooks/useMatchMedia.js';
import { useTemplate } from '@/hooks/useTemplate.js';
import { useTheme } from '@/hooks/useTheme.js';
import { styled } from '@/stitches/index.js';
import { minWidthMediaQueries } from '@/stitches/media.js';
import {
  type TemplateNames,
  type ThemeNames,
} from '@/templates/templateTypes.js';
import { themeRegistry } from '@/templates/themeRegistry.js';
import { hexToVec3 } from '@/utilities/hexToVec3.js';
import { useMount } from '@contra/react-hooks/useMount';
import { useWindowSize } from '@contra/react-hooks/useWindowSize';
import { type CSS } from '@stitches/react';
import { useAnimationFrame } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  type BufferInfo,
  createBufferInfoFromArrays,
  createProgramInfo,
  drawBufferInfo,
  type ProgramInfo,
  setBuffersAndAttributes,
  setUniforms,
} from 'twgl.js';

const BLENDMODE_MAP: Record<BlendModes, number> = {
  add: 1,
  lighten: 2,
  mix: 3,
  multiply: 0,
};

const StyledCanvas = styled('canvas', {
  height: '100vh',
  left: 0,
  top: 0,
  variants: {
    isVisible: {
      false: {
        opacity: 0,
      },
    },
    position: {
      absolute: {
        left: '50%',
        position: 'absolute',
        transform: 'translateX(-50vw)',
        width: '100vw',
      },
      fixed: {
        position: 'fixed',
      },
    },
  },
  width: '100%',
  zIndex: -10,
});

export const Canvas = ({
  className,
  css,
  positionOverride,
  templateOverride,
  themeOverride,
}: {
  readonly className?: string;
  readonly css?: CSS;
  readonly positionOverride?: 'absolute' | 'fixed';
  readonly templateOverride?: TemplateNames;
  readonly themeOverride?: ThemeNames;
}) => {
  const template = useTemplate();
  const theme = useTheme();
  const resolvedTemplate = templateOverride ?? template.current;
  const resolvedTheme = themeOverride ?? theme.current;
  const options = useMemo(
    () => themeRegistry[resolvedTemplate].canvasOptions?.[resolvedTheme].config,
    [resolvedTemplate, resolvedTheme],
  );
  const [customOptions, setCustomOptions] =
    useState<CanvasConfigByDevice | null>(options);

  const element = useRef<HTMLCanvasElement>(null);
  const isBp2 = useMatchMedia(minWidthMediaQueries.bp2);
  const {
    data: { customThemeConfig },
  } = useCustomThemeConfig();
  const [gl, setGl] = useState<WebGLRenderingContext | null>(null);
  const [bufferInfo, setBufferInfo] = useState<BufferInfo | null>(null);
  const [programInfo, setProgramInfo] = useState<ProgramInfo | null>(null);
  const { height: windowHeight, width: windowWidth } = useWindowSize();

  const optionsDeviceKey = useMemo<keyof CanvasConfigByDevice>(
    () => (isBp2 ? 'desktop' : 'mobile'),
    [isBp2],
  );

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (!customOptions || !options) return;

    if (!element.current) return;

    const glContent = element?.current?.getContext('webgl', {
      alpha: false,
      premultipliedAlpha: false,
    });

    if (
      glContent === null ||
      themeRegistry.barcelona.canvasOptions === undefined ||
      themeRegistry.losAngeles.canvasOptions === undefined ||
      resolvedTemplate === undefined ||
      themeRegistry[resolvedTemplate].canvasOptions === undefined
    )
      return;

    switch (customThemeConfig?.backgroundStyleName) {
      case 'GRADIENT_A':
        setCustomOptions(themeRegistry.barcelona.canvasOptions.dawn.config);
        break;
      case 'GRADIENT_B':
        setCustomOptions(themeRegistry.losAngeles.canvasOptions.dawn.config);
        break;
      case 'GRADIENT_C':
        setCustomOptions(themeRegistry.losAngeles.canvasOptions.dusk.config);
        break;
      case 'LOS_ANGELES_B':
        setCustomOptions(themeRegistry.losAngeles.canvasOptions.dawn.config);
        break;
      case 'BARCELONA_A':
        setCustomOptions(themeRegistry.barcelona.canvasOptions.dawn.config);
        break;
      case 'TOKYO_A':
        setCustomOptions(themeRegistry.tokyo.canvasOptions.dawn.config);
        break;

      default:
        setCustomOptions(options);
        break;
    }

    setGl(glContent);
    if (glContent === null) return;
    setProgramInfo(
      createProgramInfo(glContent, [
        vertex,
        customOptions[optionsDeviceKey].fragment as string,
      ]),
    );
  }, [
    customOptions,
    customThemeConfig,
    gl,
    options,
    optionsDeviceKey,
    resolvedTemplate,
    resolvedTheme,
    setCustomOptions,
    setGl,
    setProgramInfo,
  ]);

  useMount(() => {
    if (!customOptions) return;
    if (!element.current) return;
    const glContent = element.current.getContext('webgl', {
      alpha: false,
      premultipliedAlpha: false,
    });

    setGl(glContent);

    if (glContent === null) return;

    setProgramInfo(
      createProgramInfo(glContent, [
        vertex,
        customOptions[optionsDeviceKey].fragment as string,
      ]),
    );

    const arrays = {
      position: [-1, -1, 0, 1, -1, 0, -1, 1, 0, -1, 1, 0, 1, -1, 0, 1, 1, 0],
    };
    setBufferInfo(createBufferInfoFromArrays(glContent, arrays));
  });

  // eslint-disable-next-line complexity
  useAnimationFrame((time) => {
    const canvasConfig = customOptions?.[optionsDeviceKey];
    if (
      !gl?.canvas ||
      !customOptions ||
      !programInfo ||
      !bufferInfo ||
      !canvasConfig ||
      customThemeConfig?.backgroundStyleName === 'NONE'
    )
      return;

    const uniforms = {
      alpha: canvasConfig['alpha'] ?? 1,
      backgroundColor: customThemeConfig?.backgroundColor
        ? hexToVec3(customThemeConfig.backgroundColor)
        : hexToVec3(canvasConfig['backgroundColor'] ?? undefined),
      backgroundColorB: hexToVec3(
        canvasConfig['backgroundColorB'] ?? '#000000',
      ),
      blendMode: canvasConfig.blendMode
        ? BLENDMODE_MAP[canvasConfig.blendMode]
        : undefined,
      blurA: canvasConfig['blobA'].blur,
      blurB: canvasConfig['blobB'].blur,
      blurC: canvasConfig['blobC']?.blur ?? 0,
      colorA: hexToVec3(canvasConfig['blobA'].color),
      colorB: hexToVec3(canvasConfig['blobB'].color),
      colorC: hexToVec3(canvasConfig['blobC']?.color ?? '#ffffff'),
      maskColor: hexToVec3(canvasConfig['maskColor'] ?? '#ffffff'),
      mouse: [
        store.smoothPointer.glNormalized.x,
        store.smoothPointer.glNormalized.y,
      ],
      mouseIntensityA: isBp2 ? canvasConfig['blobA'].mouseIntensity : 0,
      mouseIntensityB: isBp2 ? canvasConfig['blobB'].mouseIntensity : 0,
      mouseIntensityC: isBp2 ? canvasConfig['blobC']?.mouseIntensity ?? 0 : 0,
      noiseIntensity: canvasConfig['noiseIntensity'],
      positionA: [
        canvasConfig['blobA'].position.x,
        canvasConfig['blobA'].position.y,
      ],
      positionB: [
        canvasConfig['blobB'].position.x,
        canvasConfig['blobB'].position.y,
      ],
      positionC: [
        canvasConfig['blobC']?.position.x ?? 0,
        canvasConfig['blobC']?.position.y ?? 0,
      ],
      radiusA:
        canvasConfig['blobA'].radius *
        (isBp2 || canvasConfig.fragment === radialGradientBlobs ? 1 : 0.25),
      radiusB:
        canvasConfig['blobB'].radius *
        (isBp2 || canvasConfig.fragment === radialGradientBlobs ? 1 : 0.25),
      radiusC:
        canvasConfig['blobC']?.radius *
          (isBp2 || canvasConfig.fragment === radialGradientBlobs ? 1 : 0.25),
      resolution: [gl.canvas.width, gl.canvas.height],
      screenRatio: windowWidth / windowHeight,
      time: time * 0.005,
    };

    gl.useProgram(programInfo.program);
    setBuffersAndAttributes(gl, programInfo, bufferInfo);
    setUniforms(programInfo, uniforms);
    drawBufferInfo(gl, bufferInfo);
  });

  if (!customOptions) {
    // eslint-disable-next-line no-console
    console.warn('Canvas settings not present for theme');
    return null;
  }

  return (
    <StyledCanvas
      // eslint-disable-next-line react/forbid-component-props
      className={className}
      css={css}
      isVisible={
        (themeRegistry[resolvedTemplate].canvasOptions?.[resolvedTheme]
          .enable &&
          customThemeConfig?.backgroundStyleName !== 'NONE') ||
        (customThemeConfig?.backgroundStyleName !== 'NONE' &&
          customThemeConfig?.backgroundStyleName !== 'DEFAULT' &&
          customThemeConfig?.backgroundStyleName !== undefined)
      }
      position={
        positionOverride
          ? positionOverride
          : customOptions
            ? customOptions?.[optionsDeviceKey]['position']
            : undefined
      }
      ref={element}
    />
  );
};
